import { clsx } from 'clsx';
import { Fragment, useMemo } from 'react';

import { Flex } from '@repo/ui/Flex';
import { Button } from '@repo/ui/Button';
import s from './ReviewItemSummaryTask.module.css';
import { useWorkflow } from '../../../workflow/hooks';
import { type TaskComponentType } from '../../../workflow/schemaTypes';
import { ItemCard } from '../../ItemCard/ItemCard';
import { type LanguageCore, Text } from '../../Language/Language';
import { useItems } from '../../../hooks/useItems';
import { useReasons } from '../../../hooks/useReasons';
import { getResolutionId } from '../../../utils/resolution';
import { useResolutions } from '../../../hooks/useResolutions';
import { ExchangeVariantCard } from '../../ExchangeVariantCard/ExchangeVariantCard';
import { buildItemSummary } from './helpers';
import {
  groupItemsByShipmentScope,
  addShipmentScopeItemsToSubmitData,
} from '../../../utils/shipmentScopeUtils';
import { ShipmentScopeItemGroup } from './ShipmentScopeItemGroup';
import type { OrderItem } from '../../../graphql/query/getOrder';

const DetailItem = ({
  title,
  subtitle,
}: {
  title: LanguageCore;
  subtitle: LanguageCore;
}) => (
  <Flex direction="row" gap="g-4" className={s.detailItem}>
    <strong className={s.title}>
      <Text {...title} />
      {':'}
    </strong>
    <Text {...subtitle} />
  </Flex>
);

export const ReviewItemSummaryTask: TaskComponentType = () => {
  const { data, goTo, order } = useWorkflow();

  // Add virtual items for shipment scope reasons to get complete data for review
  const completeData = useMemo(() => {
    return addShipmentScopeItemsToSubmitData(data, order);
  }, [data, order]);

  const items = useItems();
  const reasonsByItem = useReasons();
  const resolutionsByItem = useResolutions();

  const itemsByResolution = useMemo(() => {
    const list = items ?? [];
    return list.reduce<Record<string, OrderItem[]>>((acc, item) => {
      const resolutionId = getResolutionId(completeData, item.id)!;
      if (!acc[resolutionId]) {
        acc[resolutionId] = [];
      }
      acc[resolutionId].push(item);
      return acc;
    }, {});
  }, [items, completeData]);

  // Group items by resolution
  const resolutionGroups = useMemo(() => {
    return Object.entries(itemsByResolution).map(([resolutionId, items]) => ({
      resolutionId,
      items,
    }));
  }, [itemsByResolution]);

  if (!items || items.length === 0)
    return (
      <div>An error occurred retrieving the items for the Review Summary.</div>
    );

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    goTo('reasons');
  };

  const getHeaderFromResolution = (resolutionId: string, count: number) => {
    const baseKey = `Global.review.items.header.${count === 1 ? 'singular' : 'plural'}`;
    const primaryKey = `${baseKey}.resolution.${resolutionId}`;
    return [primaryKey, baseKey, 'Global.review.items.header'];
  };

  return (
    <Flex
      className={clsx(s.container, s.reviewItemSummaryTask)}
      direction="column"
      gap="g-24"
    >
      {resolutionGroups.map(({ resolutionId, items: groupedItems }) => {
        // Group items within this resolution by shipment scope
        const shipmentGroups = order
          ? groupItemsByShipmentScope(completeData, order, groupedItems)
          : groupedItems.map((item) => ({
              isShipmentScope: false,
              primaryItem: item,
              otherItems: [],
              reason: undefined,
              resolution: undefined,
            }));

        // Calculate total count including shipment scope items
        const totalItemCount = shipmentGroups.reduce((total, group) => {
          return total + 1 + group.otherItems.length; // +1 for primary item
        }, 0);

        const headerLangKeys = getHeaderFromResolution(
          resolutionId,
          totalItemCount,
        );

        return (
          <Flex key={`group-${resolutionId}`} direction="column" gap="g-16">
            <h2 className={s.header}>
              <Text
                langKey={headerLangKeys}
                replacementTokens={{ itemCount: `${totalItemCount}` }}
              />
            </h2>
            {shipmentGroups.map((group) => {
              if (group.isShipmentScope) {
                return (
                  <ShipmentScopeItemGroup
                    key={`shipment-${group.primaryItem.id}`}
                    primaryItem={group.primaryItem}
                    otherItems={group.otherItems}
                    reason={group.reason}
                    resolution={group.resolution}
                    onEdit={handleEdit}
                    formData={completeData}
                  />
                );
              } else {
                // Regular item - use existing logic
                const summary = buildItemSummary(
                  group.primaryItem,
                  resolutionId,
                  completeData,
                  reasonsByItem,
                  resolutionsByItem,
                );
                if (!summary || !group.primaryItem) return null;
                const {
                  selectedVariant,
                  selectedReason,
                  selectedResolution,
                  additionalDetails,
                } = summary;

                return (
                  <Fragment key={group.primaryItem.id}>
                    <ItemCard
                      item={group.primaryItem}
                      additionalDetails={additionalDetails}
                      collapsible
                      defaultCollapsed
                      imageSize="sm"
                    >
                      {(selectedReason || selectedResolution) && (
                        <Flex
                          className={s.details}
                          padding="p-24"
                          direction="column"
                        >
                          {selectedReason && (
                            <DetailItem
                              title={{
                                langKey: `Global.reviewItemSummary.reason.label`,
                              }}
                              subtitle={{
                                langKey: `Global.reason.${selectedReason?.code}.title`,
                                fallback: selectedReason?.title,
                              }}
                            />
                          )}

                          {selectedVariant && (
                            <ExchangeVariantCard item={group.primaryItem} />
                          )}

                          {selectedResolution && (
                            <DetailItem
                              title={{
                                langKey: `Global.reviewItemSummary.resolution.label`,
                              }}
                              subtitle={{
                                langKey: `Global.reviewItemSummary.resolution.${selectedResolution}`,
                              }}
                            />
                          )}
                          <Button
                            variant="secondary"
                            size="lg"
                            onClick={handleEdit}
                            className={s.edit}
                            isFullWidth
                          >
                            <Text
                              langKey={[
                                'Global.review.items.edit',
                                'Global.edit',
                              ]}
                            />
                          </Button>
                        </Flex>
                      )}
                    </ItemCard>
                  </Fragment>
                );
              }
            })}
          </Flex>
        );
      })}
    </Flex>
  );
};
